<template>
  <div
    v-show="showDayView"
    :class="[
      style[calendarClass],
      style['vdp-datepicker-calendar'],
      { [style['vdp-datepicker-calendar-date-range']]: useDateRange }
    ]"
    :style="calendarStyle"
    @mousedown.prevent
  >
    <div :class="style['vdp-header']">
      <p
        :class="[
          style['vdp-prev'],
          {
            [style.disabled]: isRtl
              ? isNextMonthDisabled(pageTimestamp)
              : isPreviousMonthDisabled(pageTimestamp)
          }
        ]"
        @click="isRtl ? nextMonth() : previousMonth()"
      />
      <p :class="style['vdp-day-month-btn']" @click="showMonthCalendar">
        {{ isYmd ? currYearName : currMonthName }} {{ isYmd ? currMonthName : currYearName }}
      </p>
      <p
        :class="[
          style['vdp-next'],
          {
            [style.disabled]: isRtl
              ? isPreviousMonthDisabled(pageTimestamp)
              : isNextMonthDisabled(pageTimestamp)
          }
        ]"
        @click="isRtl ? previousMonth() : nextMonth()"
      />
    </div>
    <ul :class="style['vdp-calendar-wrapper']">
      <li v-for="d in daysOfWeek" :key="d.timestamp" :class="[style.cell, style['day-header']]">
        {{ d }}
      </li>
      <template v-if="blankDays > 0">
        <li
          v-for="d in blankDays"
          :key="d.timestamp"
          :class="[style.cell, style.day, style.blank]"
        ></li>
      </template>
      <li
        v-for="day in days"
        :key="day.timestamp"
        :class="[style.cell, style.day, dayClasses(day)]"
        @click="selectDate(day)"
      >
        <p v-theme-light="dayRangeStyle(day)">
          <span v-theme="dayThemeStyle(day)">{{ day.date }}</span>
        </p>
        <small v-if="inventory && inventory[day.dateString] <= 5">{{
          inventory[day.dateString]
        }}</small>
      </li>
    </ul>

    <p v-if="useDateRange" :class="style.notification">{{ notification }}</p>
  </div>
</template>
<script>
import DateUtils from '../utils/DateUtils'
import { Chat } from 'src/apis'
import style from '../../../scss/_bottompanel.scss'
import { useDatepickerStore } from 'Store/datepicker'

export default {
  setup() {
    return {
      datepickerStore: useDatepickerStore()
    }
  },

  props: {
    datePickerWidgetId: Number,
    showDayView: Boolean,
    selectedDate: Date,
    pageDate: Date,
    pageTimestamp: Number,
    fullMonthName: Boolean,
    allowedToShowView: Function,
    disabledDates: Object,
    enabledDates: Object,
    highlighted: Object,
    calendarClass: String,
    calendarStyle: Object,
    translation: Object,
    isRtl: Boolean,
    mondayFirst: Boolean,
    useDateRange: Boolean
  },
  data() {
    return {
      clickCount: this.highlighted.from && this.highlighted.to ? 2 : 1,
      notification: this.$t('date_range.select'),
      style
    }
  },
  computed: {
    /**
     * Returns an array of day names
     * @return {String[]}
     */
    daysOfWeek() {
      if (this.mondayFirst) {
        const tempDays = this.translation.days.slice()
        tempDays.push(tempDays.shift())
        return tempDays
      }
      return this.translation.days
    },
    /**
     * Returns the day number of the week less one for the first of the current month
     * Used to show amount of empty cells before the first in the day calendar layout
     * @return {Number}
     */
    blankDays() {
      const d = this.pageDate
      const dObj = new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
      if (this.mondayFirst) {
        return dObj.getDay() > 0 ? dObj.getDay() - 1 : 6
      }
      return dObj.getDay()
    },
    /**
     * @return {Object[]}
     */
    days() {
      const d = this.pageDate
      const days = []
      // set up a new date object to the beginning of the current 'page'
      const dObj = new Date(d.getFullYear(), d.getMonth(), 1, d.getHours(), d.getMinutes())
      const daysInMonth = DateUtils.daysInMonth(dObj.getFullYear(), dObj.getMonth())
      for (let i = 0; i < daysInMonth; i++) {
        days.push({
          date: dObj.getDate(),
          dateString: DateUtils.formatDate(dObj, 'yyyy/MM/dd'),
          timestamp: dObj.getTime(),
          isSelected: this.isSelectedDate(dObj),
          isDisabled: this.isDisabledDate(dObj),
          isHighlighted: this.isHighlightedDate(dObj),
          isHighlightStart: this.isHighlightStart(dObj),
          isHighlightEnd: this.isHighlightEnd(dObj),
          isToday: dObj.toDateString() === new Date().toDateString(),
          isWeekend: dObj.getDay() === 0 || dObj.getDay() === 6,
          isSaturday: dObj.getDay() === 6,
          isSunday: dObj.getDay() === 0
        })
        dObj.setDate(dObj.getDate() + 1)
      }
      return days
    },
    /**
     * Gets the name of the month the current page is on
     * @return {String}
     */
    currMonthName() {
      const monthName = this.fullMonthName ? this.translation.months : this.translation.monthsAbbr
      return DateUtils.getMonthNameAbbr(this.pageDate.getMonth(), monthName)
    },
    /**
     * Gets the name of the year that current page is on
     * @return {Number}
     */
    currYearName() {
      const yearSuffix = this.translation.yearSuffix
      return `${this.pageDate.getFullYear()}${yearSuffix}`
    },
    /**
     * Is this translation using year/month/day format?
     * @return {Boolean}
     */
    isYmd() {
      return this.translation.ymd && this.translation.ymd === true
    },
    /**
     * return the date-wise inventory count
     * @return {Object || null}
     */
    inventory() {
      return this.datepickerStore.inventory
    },
    hasEnabledDates() {
      return this.enabledDates && this.enabledDates.dates && this.enabledDates.dates.length
    },
    clickCountEven() {
      return this.clickCount % 2 === 0
    }
  },
  methods: {
    selectDate(date) {
      if (date.isDisabled) {
        this.$emit('selectedDisabled', date)
        return false
      }
      this.selectDateRange(date)
      this.$emit('selectDate', date.timestamp)
    },
    selectDateRange(date) {
      if (!this.useDateRange) return

      this.clickCount += 1
      if (this.clickCountEven) {
        let from = this.selectedDate
        let to = new Date(date.timestamp)

        // Swap from and to if need be
        if (from > to) [from, to] = [to, from]
        // Ignore from === to
        if (from.getTime() === to.getTime()) {
          this.clickCount -= 1
          return
        }

        for (let d = from.getDate() - 1; d < to.getDate(); d++) {
          if (this.days[d].isDisabled) {
            this.notification = this.$t('date_range.no_disabled_dates')
            this.clickCount -= 1
            return
          }
        }

        this.notification = this.$t('date_range.send')
        this.$emit('highlightDates', { from, to })
        this.datepickerStore.setDateRange([
          DateUtils.formatDate(from, 'yyyy-MM-dd'),
          DateUtils.formatDate(to, 'yyyy-MM-dd')
        ])
      } else {
        this.notification = this.$t('date_range.select')
        this.$emit('highlightDates', {})
        this.datepickerStore.setDateRange([])
      }
    },
    /**
     * @return {Number}
     */
    getPageMonth() {
      return this.pageDate.getMonth()
    },
    /**
     * Emit an event to show the month picker
     */
    showMonthCalendar() {
      this.$emit('showMonthCalendar')
    },
    /**
     * Change the page month
     * @param {Number} incrementBy
     */
    changeMonth(incrementBy) {
      const date = this.pageDate
      date.setMonth(date.getMonth() + incrementBy)
      this.$emit('changedMonth', date)
      return date
    },
    /**
     * Decrement the page month
     */
    previousMonth() {
      if (!this.isPreviousMonthDisabled()) {
        this.changeMonth(-1)
      }
    },
    /**
     * Is the previous month disabled?
     * @return {Boolean}
     */
    isPreviousMonthDisabled() {
      if (!this.disabledDates || !this.disabledDates.to || this.hasEnabledDates) {
        return false
      }
      const d = this.pageDate
      return (
        this.disabledDates.to.getMonth() >= d.getMonth() &&
        this.disabledDates.to.getFullYear() >= d.getFullYear()
      )
    },
    /**
     * Increment the current page month
     */
    nextMonth() {
      if (!this.isNextMonthDisabled()) {
        const date = this.changeMonth(+1)
        this.getNextMonthInventory(date)
      }
    },
    /**
     * Is the next month disabled?
     * @return {Boolean}
     */
    isNextMonthDisabled() {
      if (!this.disabledDates || !this.disabledDates.from || this.hasEnabledDates) {
        return false
      }
      const d = this.pageDate
      return (
        this.disabledDates.from.getMonth() <= d.getMonth() &&
        this.disabledDates.from.getFullYear() <= d.getFullYear()
      )
    },
    /**
     * Whether a day is selected
     * @param {Date}
     * @return {Boolean}
     */
    isSelectedDate(dObj) {
      return this.selectedDate && this.selectedDate.toDateString() === dObj.toDateString()
    },
    /**
     * Whether a day is disabled
     * @param date {Date}
     * @return {Boolean}
     */
    isDisabledDate(date) {
      let disabledDates = false

      if (!this.disabledDates && !this.enabledDates) return false

      if (this.disabledDates.dates && this.disabledDates.dates.length) {
        this.disabledDates.dates.forEach((dStr) => {
          const d = new Date(dStr)
          if (date.toDateString() === d.toDateString()) {
            disabledDates = true
            return true
          }
        })
      }

      if (this.hasEnabledDates) {
        const enabledDates = this.enabledDates.dates.map((dStr) => new Date(dStr).toDateString())
        disabledDates = !enabledDates.includes(date.toDateString())
      }

      if (this.disabledDates.to && date < this.disabledDates.to && !this.hasEnabledDates) {
        disabledDates = true
      }
      if (this.disabledDates.from && date > this.disabledDates.from && !this.hasEnabledDates) {
        disabledDates = true
      }
      if (this.disabledDates.ranges) {
        this.disabledDates.ranges.forEach((range) => {
          if (range.from && range.to) {
            if (date < range.to && date > range.from) {
              disabledDates = true
              return true
            }
          }
        })
      }
      if (this.disabledDates.days && this.disabledDates.days.indexOf(date.getDay()) !== -1) {
        disabledDates = true
      }
      if (
        this.disabledDates.daysOfMonth &&
        this.disabledDates.daysOfMonth.indexOf(date.getDate()) !== -1
      ) {
        disabledDates = true
      }
      if (
        typeof this.disabledDates.customPredictor === 'function' &&
        this.disabledDates.customPredictor(date)
      ) {
        disabledDates = true
      }
      return disabledDates
    },
    /**
     * Whether a day is highlighted (only if it is not disabled already except when highlighted.includeDisabled is true)
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightedDate(date) {
      if (!(this.highlighted && this.highlighted.includeDisabled) && this.isDisabledDate(date)) {
        return false
      }

      let highlighted = false

      if (!this.highlighted) {
        return false
      }

      if (this.highlighted.dates) {
        this.highlighted.dates.forEach((d) => {
          if (date.toDateString() === d.toDateString()) {
            highlighted = true
            return true
          }
        })
      }

      if (this.isDefined(this.highlighted.from) && this.isDefined(this.highlighted.to)) {
        highlighted = date >= this.highlighted.from && date <= this.highlighted.to
      }

      if (
        typeof this.highlighted.days !== 'undefined' &&
        this.highlighted.days.indexOf(date.getDay()) !== -1
      ) {
        highlighted = true
      }

      if (
        typeof this.highlighted.daysOfMonth !== 'undefined' &&
        this.highlighted.daysOfMonth.indexOf(date.getDate()) !== -1
      ) {
        highlighted = true
      }

      if (
        typeof this.highlighted.customPredictor === 'function' &&
        this.highlighted.customPredictor(date)
      ) {
        highlighted = true
      }

      return highlighted
    },
    dayClasses(day) {
      return {
        [style['selected']]: day.isSelected,
        [style['disabled']]: day.isDisabled,
        [style['highlighted']]: day.isHighlighted,
        [style['highlight-start']]: day.isHighlightStart,
        [style['highlight-end']]: day.isHighlightEnd
      }
    },
    dayThemeStyle(day) {
      return day.isSelected || day.isHighlighted
    },
    dayRangeStyle(day) {
      return day.isHighlighted
    },

    /**
     * Whether a day is highlighted and it is the first date
     * in the highlighted range of dates
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightStart(date) {
      return (
        this.isHighlightedDate(date) &&
        this.highlighted.from instanceof Date &&
        this.highlighted.from.getFullYear() === date.getFullYear() &&
        this.highlighted.from.getMonth() === date.getMonth() &&
        this.highlighted.from.getDate() === date.getDate()
      )
    },
    /**
     * Whether a day is highlighted and it is the first date
     * in the highlighted range of dates
     * @param {Date}
     * @return {Boolean}
     */
    isHighlightEnd(date) {
      return (
        this.isHighlightedDate(date) &&
        this.highlighted.to instanceof Date &&
        this.highlighted.to.getFullYear() === date.getFullYear() &&
        this.highlighted.to.getMonth() === date.getMonth() &&
        this.highlighted.to.getDate() === date.getDate()
      )
    },
    /**
     * Helper
     * @param  {mixed}  prop
     * @return {Boolean}
     */
    isDefined(prop) {
      return typeof prop !== 'undefined' && prop
    },
    getNextMonthInventory(date) {
      if (!this.datepickerStore.inventory) return
      const d = DateUtils.formatDate(date, 'yyyy/MM/dd')
      if (!this.datepickerStore.inventory[d]) {
        Chat.bestRoomAction('get_inventory', this.datePickerWidgetId, { next_month: d })
          .then((payload) => {
            this.datepickerStore.appendInventory(payload.inventory)
            this.datepickerStore.appendDisabledDates(payload.disabled_dates)
          })
          .catch((err) => {
            console.error('failed to get next month inventory', err)
          })
      }
    }
  }
}
// eslint-disable-next-line
</script>
